import useResizeObserver from "@react-hook/resize-observer";
import { useEffect, useMemo, useRef, useState } from "react";

import { hoverLegendBaseAtom } from "atoms/interaction";
import { useSetAtom } from "jotai";
import debounce from "lodash/debounce";
import styled from "styled-components";

import { FocusTreeMapInputModel } from "models/LegendItem";

import { ErrorBoundary } from "components/base";

export default function FocusTreeMap({
  items,
  onChange
}: FocusTreeMapInputModel): JSX.Element {
  const setHoverLegend = useSetAtom(hoverLegendBaseAtom);

  // ECharts fires "hover" synchronously on every pointer-move frame, which
  // would produce a Jotai write — and a full React re-render of every
  // chart subscriber — at pointer frequency (~60 fps). A 100 ms debounce
  // collapses rapid transitions into a single write once the pointer settles,
  // matching the original Redux debounce cadence and eliminating the
  // ECharts rebuild cascade visible in the profiler.
  // setHoverLegend is a stable reference from useSetAtom; empty deps are intentional.
  const debouncedSetHoverLegend = useMemo(
    () =>
      debounce((name: string) => {
        setHoverLegend({
          item: name,
          id: "",
          group: "",
          itemUnfiltered: "",
          isMultiProductChart: false
        });
      }, 100),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  useEffect(
    () => () => {
      debouncedSetHoverLegend.cancel();
    },
    [debouncedSetHoverLegend]
  );

  const itemsRef = useRef(items);
  const chartIdRef = useRef<Maybe<HTMLElement>>(null);
  const myChartRef = useRef(null);
  const [treemapOptions, setTreeMapOptions] = useState(undefined);

  useEffect(() => {
    itemsRef.current = items;
    setTreeMapOptions({
      chartType: "treemap",
      tooltip: {},
      breadcrumb: {
        show: false
      },
      series: [
        {
          breadcrumb: {
            show: false
          },
          name: "",
          type: "treemap",
          nodeClick: false,
          label: {
            show: true,
            formatter: "{b}"
          },
          itemStyle: {
            borderColor: "#fff"
          },
          levels: [
            {
              itemStyle: {
                borderColor: "#555",
                borderWidth: 4,
                gapWidth: 4
              }
            },
            {
              colorSaturation: [0.3, 0.6],
              itemStyle: {
                borderColorSaturation: 0.7,
                gapWidth: 2,
                borderWidth: 2
              }
            },
            {
              colorSaturation: [0.3, 0.5],
              itemStyle: {
                borderColorSaturation: 0.6,
                gapWidth: 1
              }
            },
            {
              colorSaturation: [0.3, 0.5]
            }
          ],

          data: items.map((li) => {
            return {
              value: li.count,
              name: li.value,
              select: false,
              itemStyle: {
                color: li.inFilter ? li.color : "#cfcfcf44"
              },
              path: li.property
            };
          })
        }
      ]
    });
  }, [items]);

  useEffect(() => {
    if (!chartIdRef.current) return;

    import("hd-echarts/echarts").then((echarts) => {
      if (!chartIdRef.current) return;

      myChartRef.current = echarts.init(chartIdRef.current, null, {
        renderer: "canvas",
        width: "auto",
        height: "auto"
      });

      const chart = myChartRef.current;

      // eslint-disable-next-line listeners/no-missing-remove-event-listener, listeners/no-inline-function-event-listener -- ECharts uses its own .on()/.off() API, not DOM addEventListener/removeEventListener. Cleanup is handled via chart.off() + chart.dispose() in the useEffect return.
      chart.on("hover", (e) => {
        const index = e.dataIndex - 1;
        const seriesIndex = e.seriesIndex;
        if (e.seriesIndex >= 0) {
          const option = chart.getOption();
          const series = option.series[seriesIndex];
          if (series.data.length > index) {
            const data = series.data[index];
            if (data) {
              const seriesName = data.name;
              debouncedSetHoverLegend(seriesName);
            }
          }
        }
      });

      // eslint-disable-next-line listeners/no-missing-remove-event-listener, listeners/no-inline-function-event-listener -- ECharts uses its own .on()/.off() API, not DOM addEventListener/removeEventListener. Cleanup is handled via chart.off() + chart.dispose() in the useEffect return.
      chart.on("click", (e) => {
        const index = e.dataIndex - 1;
        const seriesIndex = e.seriesIndex;
        if (e.seriesIndex >= 0) {
          const option = chart.getOption();
          const series = option.series[seriesIndex];
          if (series.data.length > index) {
            const data = series.data[index];
            if (data) {
              const seriesName = data.name;
              const matches = itemsRef.current.filter((item) => item.value == seriesName);
              if (matches.length > 0) {
                onChange && onChange(matches);
              }
            }
          }
        }
      });

      // Set initial options if they exist
      if (treemapOptions) {
        chart.setOption(treemapOptions);
      }
    });

    return () => {
      if (myChartRef.current) {
        myChartRef.current.off("hover");
        myChartRef.current.off("click");
        myChartRef.current.dispose();
        myChartRef.current = null;
      }
    };
  }, [treemapOptions]);

  useResizeObserver(chartIdRef, () => {
    if (myChartRef.current) {
      myChartRef.current.resize();
    }
  });

  return (
    <ErrorBoundary>
      <RootContainer ref={chartIdRef}></RootContainer>
    </ErrorBoundary>
  );
}

const RootContainer = styled.div`
  max-width: 100%;
  min-width: 100%;
  min-height: 0;
  min-height: 100%;
  overflow-x: hidden;
  svg {
    text {
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;
    }
  }
`;
